/* POSHRADS — header.js
   Disclosure navigation (mega menus / drawer accordions), mobile drawer, search shell.
   Vanilla, event-driven, no dependencies, no scroll listeners.
   Architecture: docs/header-navigation.md */
(() => {
  'use strict';

  const DESKTOP = window.matchMedia('(min-width: 1024px)');
  const OPEN_DELAY = 80;
  const CLOSE_DELAY = 200;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  class PoshHeader {
    constructor(root) {
      this.root = root;
      this.drawer = root.querySelector('[data-drawer]');
      this.panel = root.querySelector('[data-drawer-panel]');
      this.drawerButton = root.querySelector('[data-drawer-open]');
      this.search = root.querySelector('[data-search]');
      this.searchButton = root.querySelector('[data-search-toggle]');
      this.openItem = null;
      this.timer = null;
      this.inerted = [];

      // Stable handler references, so destroy() can remove every listener it added.
      this.handlers = {
        click: this.onClick.bind(this),
        keydown: this.onKeydown.bind(this),
        focusout: this.onFocusout.bind(this),
      };
      this.onDocumentPointer = this.onDocumentPointer.bind(this);
      this.onMediaChange = () => this.reset();
      this.hoverListeners = [];

      Object.entries(this.handlers).forEach(([type, handler]) => root.addEventListener(type, handler));
      root.querySelectorAll('[data-mega-item]').forEach((item) => {
        const enter = (event) => this.onHover(event, item, true);
        const leave = (event) => this.onHover(event, item, false);
        item.addEventListener('pointerenter', enter);
        item.addEventListener('pointerleave', leave);
        this.hoverListeners.push([item, enter, leave]);
      });
      DESKTOP.addEventListener('change', this.onMediaChange);
    }

    // Mega menus (desktop)

    triggerOf(item) {
      return item.querySelector(':scope > [data-disclosure="mega"]');
    }

    setMega(item, open) {
      clearTimeout(this.timer);
      if (this.openItem && this.openItem !== item) {
        this.triggerOf(this.openItem).setAttribute('aria-expanded', 'false');
        this.openItem = null;
      }
      if (!item) return;
      this.triggerOf(item).setAttribute('aria-expanded', String(open));
      this.openItem = open ? item : null;
      if (open) this.closeSearch(false);
      document[open ? 'addEventListener' : 'removeEventListener']('pointerdown', this.onDocumentPointer);
    }

    closeMega(returnFocus) {
      const item = this.openItem;
      if (!item) return;
      this.setMega(item, false);
      if (returnFocus) this.triggerOf(item).focus();
    }

    onHover(event, item, entering) {
      if (!DESKTOP.matches || event.pointerType !== 'mouse') return;
      clearTimeout(this.timer);
      if (entering) {
        if (this.openItem === item) return;
        // Moving between items while a panel is open switches instantly (no flicker).
        if (this.openItem) this.setMega(item, true);
        else this.timer = setTimeout(() => this.setMega(item, true), OPEN_DELAY);
      } else if (this.openItem === item) {
        this.timer = setTimeout(() => this.setMega(item, false), CLOSE_DELAY);
      }
    }

    onDocumentPointer(event) {
      if (this.openItem && !this.openItem.contains(event.target)) this.closeMega(false);
    }

    // Events

    onClick(event) {
      const target = event.target;
      const disclosure = target.closest('[data-disclosure]');
      if (disclosure) {
        const open = disclosure.getAttribute('aria-expanded') !== 'true';
        if (disclosure.dataset.disclosure === 'mega' && DESKTOP.matches) {
          this.setMega(disclosure.closest('[data-mega-item]'), open);
        } else {
          disclosure.setAttribute('aria-expanded', String(open));
        }
        return;
      }
      if (target.closest('[data-drawer-open]')) this.openDrawer();
      else if (target.closest('[data-drawer-close]')) this.closeDrawer(true);
      else if (target.closest('[data-search-toggle]')) this.toggleSearch();
      else if (target.closest('[data-search-close]')) this.closeSearch(true);
    }

    onKeydown(event) {
      if (event.key === 'Tab' && this.isDrawerOpen()) {
        this.trapFocus(event);
        return;
      }
      if (event.key !== 'Escape') return;
      if (this.isDrawerOpen()) this.closeDrawer(true);
      else if (this.search && !this.search.hidden) this.closeSearch(true);
      else if (this.openItem) this.closeMega(true);
      else return;
      event.preventDefault();
    }

    onFocusout(event) {
      // Keyboard focus leaving an open mega menu closes it (no focus trap on desktop).
      if (this.openItem && event.relatedTarget && !this.openItem.contains(event.relatedTarget)) {
        this.setMega(this.openItem, false);
      }
    }

    // Mobile drawer (modal)

    isDrawerOpen() {
      return Boolean(this.drawer && this.drawer.classList.contains('is-open'));
    }

    openDrawer() {
      if (!this.drawer || DESKTOP.matches) return;
      this.closeSearch(false);
      this.drawer.classList.add('is-open');
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      this.panel.setAttribute('aria-labelledby', this.panel.dataset.labelledby);
      this.drawerButton.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('scroll-lock');
      this.setInert(true);
      const close = this.panel.querySelector('button[data-drawer-close]');
      (close || this.panel).focus();
    }

    closeDrawer(returnFocus) {
      if (!this.isDrawerOpen()) return;
      this.drawer.classList.remove('is-open');
      ['role', 'aria-modal', 'aria-labelledby'].forEach((name) => this.panel.removeAttribute(name));
      this.drawerButton.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('scroll-lock');
      this.setInert(false);
      if (returnFocus) this.drawerButton.focus();
    }

    // Everything outside the drawer becomes inert: not focusable, not clickable, hidden from AT.
    setInert(on) {
      if (!on) {
        this.inerted.forEach((element) => (element.inert = false));
        this.inerted = [];
        return;
      }
      let node = this.drawer;
      while (node && node !== document.body) {
        for (const sibling of node.parentElement.children) {
          if (sibling !== node && !sibling.inert) {
            sibling.inert = true;
            this.inerted.push(sibling);
          }
        }
        node = node.parentElement;
      }
    }

    trapFocus(event) {
      const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // Search shell

    toggleSearch() {
      if (!this.search) return;
      if (!this.search.hidden) {
        this.closeSearch(true);
        return;
      }
      this.closeMega(false);
      this.search.hidden = false;
      this.searchButton.setAttribute('aria-expanded', 'true');
      this.search.querySelector('input[type="search"]').focus();
    }

    closeSearch(returnFocus) {
      if (!this.search || this.search.hidden) return;
      this.search.hidden = true;
      this.searchButton.setAttribute('aria-expanded', 'false');
      if (returnFocus) this.searchButton.focus();
    }

    // Breakpoint change / editor reload

    reset() {
      clearTimeout(this.timer);
      this.closeDrawer(false);
      this.root.querySelectorAll('[data-disclosure]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
      this.openItem = null;
      document.removeEventListener('pointerdown', this.onDocumentPointer);
    }

    // Theme Editor section unload: restore page state and drop every listener this instance owns.
    destroy() {
      this.reset();
      this.closeSearch(false);
      DESKTOP.removeEventListener('change', this.onMediaChange);
      Object.entries(this.handlers).forEach(([type, handler]) => this.root.removeEventListener(type, handler));
      this.hoverListeners.forEach(([item, enter, leave]) => {
        item.removeEventListener('pointerenter', enter);
        item.removeEventListener('pointerleave', leave);
      });
      delete this.root.poshHeader;
      this.hoverListeners = [];
      this.root = this.drawer = this.panel = this.drawerButton = this.search = this.searchButton = null;
    }
  }

  const init = (scope) => {
    scope.querySelectorAll('[data-header]').forEach((root) => {
      if (!root.poshHeader) root.poshHeader = new PoshHeader(root);
    });
  };

  init(document);

  // Theme Editor support.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-header]');
    if (root && root.poshHeader) root.poshHeader.destroy();
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const root = event.target.closest('[data-header]');
    if (root && root.poshHeader) root.poshHeader.closeMega(false);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target.closest('[data-mega-item]');
    const root = event.target.closest('[data-header]');
    if (item && root && root.poshHeader && DESKTOP.matches) root.poshHeader.setMega(item, true);
  });
})();
